import React from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { 
  Banknote, 
  TrendingUp, 
  TrendingDown, 
  CreditCard,
  ArrowRight
} from 'lucide-react'
import { dashboardService } from '@/services/dashboardService'
import { formatCurrency } from '@/utils/formatters'
import { CURRENCY_SYMBOL } from '@/utils/constants'

const FinancialOverview = () => {
  const { data: financialData, isLoading } = useQuery(
    'dashboard-financial-overview',
    dashboardService.getFinancialOverview,
    {
      select: (response) => response.data,
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    }
  )

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            Financial Overview
          </h3>
          <Link
            to="/finance"
            className="text-sm text-primary-600 hover:text-primary-500 flex items-center"
          >
            View all
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <p className="text-sm text-gray-600">Revenue</p>
            <p className="text-xl font-semibold text-green-600">
              {formatCurrency(financialData?.data?.revenue || 0)}
            </p>
          </div>

          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <TrendingDown className="h-6 w-6 text-red-600" />
            </div>
            <p className="text-sm text-gray-600">Expenses</p>
            <p className="text-xl font-semibold text-red-600">
              {formatCurrency(financialData?.data?.expenses || 0)}
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <Banknote className="h-5 w-5 text-gray-500 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Net Profit</p>
                <p className="text-xs text-gray-500">This month</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-900">
                {formatCurrency(financialData?.data?.netProfit || 0)}
              </p>
              <p
                className={`text-xs ${
                  financialData?.data?.profitChange >= 0
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {financialData?.data?.profitChange >= 0 ? "+" : ""}
                {financialData?.data?.profitChange || 0}%
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <CreditCard className="h-5 w-5 text-gray-500 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">Outstanding</p>
                <p className="text-xs text-gray-500">Accounts receivable</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-900">
                {formatCurrency(financialData?.data?.outstanding || 0)}
              </p>
              <p className="text-xs text-gray-500">
                {financialData?.data?.outstandingCount || 0} invoices
              </p>
            </div>
          </div>
        </div>
        {financialData?.data?.recentTransactions &&
          financialData.recentTransactions?.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Recent Transactions
              </h4>
              <div className="space-y-2">
                {financialData.recentTransactions
                  .slice(0, 3)
                  .map((transaction, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-gray-600 truncate">
                        {transaction.description}
                      </span>
                      <span
                        className={`font-medium ${
                          transaction.type === "income"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {transaction.type === "income" ? "+" : "-"}
                        {formatCurrency(transaction.amount || 0).replace(
                          CURRENCY_SYMBOL,
                          ""
                        )}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

export default FinancialOverview